import { t } from './i18nService';

/**
 * Service for handling header accessibility (a11y) attributes and configurations
 * that are derived from the a11y iteration flag for IIB screen reader support.
 */
const HeaderA11yService = {
    /**
     * Get header notifications container attributes
     * @param {boolean} isA11yIterationForIIBScreenReaderEnabled - Whether a11y iteration is enabled
     * @returns {Object} Object containing resolved class names, tabIndex and headerRole
     */
    getHeaderNotificationsAttributes(isA11yIterationForIIBScreenReaderEnabled) {
        return {
            connectionContainerClass: isA11yIterationForIIBScreenReaderEnabled ? 'a11yMode' : '',
            centralBarClass: isA11yIterationForIIBScreenReaderEnabled ? 'a11yMode' : '',
            headerTabIndex: isA11yIterationForIIBScreenReaderEnabled ? 0 : undefined,
            headerRole: isA11yIterationForIIBScreenReaderEnabled ? 'region' : undefined,
        };
    },

    /**
     * Get message button aria label
     * ARIA labels should use a distinct naming convention (e.g., "Aria" suffix) for their translation keys.
     *
     * Rationale:
     * - Using a unique suffix like "Aria" makes it clear that the label is intended only for screen reader users,
     *   and is not visible in the UI. This helps prevent confusion between visible labels and those used solely
     *   for accessibility, both for developers and translators.
     * - It aids maintainers in quickly identifying which translations are for ARIA attributes, reducing the risk
     *   of accidentally reusing or overwriting them with visible text.
     *
     * Example:
     * - Use `header.messagesboxAria` for the aria-label attribute (screen reader only).
     * - Use `header.messagesbox` for the visible label or tooltip (UI text).
     *
     * When to use:
     * - Use the ARIA-specific translation (with "Aria" suffix) when setting attributes like `aria-label`,
     *   `aria-describedby`, etc., that are not shown visually but are read by assistive technologies.
     * - Use the regular label translation for visible UI elements.
     *
     * @param {boolean} isA11yIterationForIIBScreenReaderEnabled - Whether a11y iteration is enabled
     * @returns {string|undefined} Aria label text or undefined
     */
    getMessageButtonAriaLabel(isA11yIterationForIIBScreenReaderEnabled) {
        // Use header.messagesboxAria (not header.messagesbox) to indicate this is aria-specific
        return isA11yIterationForIIBScreenReaderEnabled ? t('header.messagesboxAria') : undefined;
    },

    /**
     * Get options menu button attributes
     * @param {boolean} isA11yIterationForIIBScreenReaderEnabled - Whether a11y iteration is enabled
     * @returns {Object} Object containing aria label and aria keyshortcuts
     */
    getOptionsMenuButtonAttributes(isA11yIterationForIIBScreenReaderEnabled) {
        return {
            ariaLabel: isA11yIterationForIIBScreenReaderEnabled ? t('options.optionsHeaderAria') : undefined,
            ariaKeyshortcuts: isA11yIterationForIIBScreenReaderEnabled ? t('options.optionsboxshortcut') : undefined,
        };
    },
};

export default HeaderA11yService;
